import React, { useState, useMemo, useRef, useLayoutEffect } from 'react'
import { object, func, string } from 'prop-types'
import classNames from 'classnames'
import { useBodyScrollLock, useOnClickOutside } from '../../../helpers/hooks'
import { filterizer } from '../../../helpers/common'
import { useWindowDimensions } from '../../../providers/WindowDimensionsProvider'
import { DEFAULT_FUNC, EMPTY_OBJECT, KEYS, RESPONSIVE_BREAKPOINTS } from '../../../utils/constants'

const flattenSuggestions = suggestions => {
  return Object.entries(suggestions)
    .reduce((filtersAcc, [filter, options]) => {
      if (Array.isArray(options)) {
        const formattedOptions = options.reduce((acc, { name, slug }) => {
          if (name) acc.push({ name, filter: `/${filter}/${slug || name}` })
          return acc
        }, [])
        return filtersAcc.concat(formattedOptions)
      }
      return filtersAcc
    }, [])
}

const Typeahead = ({
  suggestions = EMPTY_OBJECT,
  onSuggestionSelected = DEFAULT_FUNC,
  noResultsMessage = '',
  children
}) => {
  const flatSuggestions = useMemo(() => flattenSuggestions(suggestions), [suggestions])
  const [open, setOpen] = useState(false)
  const [userInput, setUserInput] = useState('')
  const [inputValue, setInputValue] = useState('')
  const [activeSuggestionIndex, setActiveSuggestionIndex] = useState(-1)
  const [matchingSuggestions, setMatchingSuggestions] = useState([])
  const [setScrollLocked] = useBodyScrollLock(false)
  const containerEl = useRef()
  const { width = window.innerWidth } = useWindowDimensions() || {}
  const reset = (e, value) => {
    setOpen(false)
    setScrollLocked(false)
    setMatchingSuggestions([])
    setActiveSuggestionIndex(-1)
    setInputValue(value || userInput)
  }
  useOnClickOutside(containerEl, reset)

  useLayoutEffect(() => {
    const fixedHeader = document.querySelector(width < RESPONSIVE_BREAKPOINTS.large
      ? '.afd-header-main__container'
      : '.jv-header__bottom--fixed'
    )
    fixedHeader && fixedHeader.classList.toggle('with-overlay', open)

    return () => {
      document.querySelectorAll('.with-overlay')
        .forEach(el => el.classList.remove('with-overlay'))
    }
  }, [open])

  const findSuggestionMatch = e => {
    const { value } = e.target
    setUserInput(value)
    let filtered = []
    if (value.length) {
      const queryFilterized = filterizer(value)
      filtered = queryFilterized(flatSuggestions).slice(0, 10)
    }
    setMatchingSuggestions(filtered)
    setInputValue(value)
    setOpen(!!value.length)
    setScrollLocked(!!value.length)
  }

  const onKeyboardNavigation = e => {
    if (![KEYS.ESCAPE, KEYS.ENTER, KEYS.ARROW_UP, KEYS.ARROW_DOWN].includes(e.key)) return false
    let activeElement = activeSuggestionIndex
    switch (e.key) {
      case KEYS.ESCAPE:
        return reset()
      case KEYS.ENTER:
        if (matchingSuggestions.length && activeSuggestionIndex === -1) return // no option has been selected
        return !matchingSuggestions.length
          ? handleNoMatch()
          : submitOption(matchingSuggestions[activeSuggestionIndex])
      case KEYS.ARROW_UP:
        activeElement -= activeElement >= 0 ? 1 : 0
        break
      case KEYS.ARROW_DOWN:
        activeElement += activeElement < matchingSuggestions.length - 1 ? 1 : 0
        break
    }
    setActiveSuggestionIndex(activeElement)
    setInputValue(activeElement < 0 ? userInput : matchingSuggestions[activeElement].name)
  }

  const submitOption = option => {
    onSuggestionSelected(option.filter)
    setUserInput(option.name)
    reset(null, option.name)
  }

  const handleNoMatch = () => onSuggestionSelected(null, userInput)

  return <div className={classNames('typeahead', { 'typeahead--active': open })} ref={containerEl}>
    { children(inputValue, findSuggestionMatch, onKeyboardNavigation) }
    {open &&
      <ul className='typeahead__options'>
        {matchingSuggestions.length
          ? matchingSuggestions.map((option, index) =>
            <li key={`typeahead-${index}`} className={index === activeSuggestionIndex ? 'active' : ''}
              onClick={() => submitOption(option)}
              dangerouslySetInnerHTML={{ __html: option.filteredName || option.name || option }} />)
          : <li className='typeahead__no-results' onClick={handleNoMatch} dangerouslySetInnerHTML={{ __html: noResultsMessage }} />
        }
      </ul>
    }
  </div>
}

Typeahead.propTypes = {
  suggestions: object,
  onSuggestionSelected: func,
  noResultsMessage: string,
  children: func
}

export default Typeahead